import { ConfigAffiliateShopping, configAffiliateShopping } from "@pub/config";
import {
  SectionHeading,
  Typography,
  useCustomClassContext,
} from "@sphtech/dmg-design-system";
import { PUBLIC_PREVIEW_TEXT } from "@src/app/constants";
import {
  AffiliateShoppingData,
  AffiliateShoppingDataBlock,
} from "@src/app/types/ServerCustomContext";
import cx from "classnames";
import { ReactNode } from "react";

import { ResolvedCUEElement } from "../Article/elements";
import Storyline from "../Article/Storyline";
import styles from "./affiliateshopping.module.css";
import { elementsWithDataSource } from "./Elements";

type AffiliateShoppingLayoutProps = {
  data: {
    path?: string;
    uniqSectionName?: string;
    heading?: string;
    headingZH?: string;
    standFirst?: string | ResolvedCUEElement[];
    showVerticalLine?: boolean;
    blocks?: AffiliateShoppingData["blocks"];
    displaySetting?: AffiliateShoppingData["displaySetting"];
  };
};

export default function AffiliateShoppingLayout({
  data,
}: AffiliateShoppingLayoutProps): ReactNode {
  const customClass = useCustomClassContext();
  const { Heading } = Typography;
  const heading = data.heading || "";
  const headingZH = data.headingZH;
  const mapLayoutElement = (
    block: AffiliateShoppingDataBlock,
    configElement: NonNullable<
      ConfigAffiliateShopping["layoutGrid"]
    >["elements"][number],
  ) => {
    const hasDataSource = "dataSource" in configElement;

    if (hasDataSource) {
      const ElementComponent = elementsWithDataSource[configElement.name];

      return (
        <ElementComponent
          data={block}
          dataSource={configElement.dataSource}
          withDivider={data.showVerticalLine && !!block.title}
        />
      );
    }
  };

  return (
    <>
      {data.displaySetting?.publicPreview && (
        <div className={styles.previewMode}>{PUBLIC_PREVIEW_TEXT}</div>
      )}
      <section className={styles.introduction}>
        {headingZH ? (
          <Heading.SectionTitle nameZH={headingZH}>
            {heading}
          </Heading.SectionTitle>
        ) : (
          <SectionHeading title={heading} withDivider={false} />
        )}
        {Array.isArray(data.standFirst) ? (
          <div className={styles.introText}>
            <Storyline
              bodyMaxWidth={1000}
              path={data.path || ""}
              elements={data.standFirst}
            />
          </div>
        ) : (
          data.standFirst
        )}
      </section>

      {configAffiliateShopping.layoutGrid &&
        data.blocks?.map((block, k) => {
          const selectedType = block.selectedType;
          const configElement =
            configAffiliateShopping.layoutGrid?.elements.find((element) => {
              const hasDataSource = "dataSource" in element;

              if (hasDataSource && selectedType) {
                const dataSource = element.dataSource;
                return selectedType === "list"
                  ? element.dataSource.key === block.key
                  : dataSource.types.includes(selectedType) &&
                      dataSource.key === block.key;
              }
            });

          if (configElement) {
            const hasDataSource = "dataSource" in configElement;

            const configBgColor =
              (hasDataSource &&
                configElement.dataSource.config?.backgroundColor) ||
              "";

            return (
              <section
                className={cx(
                  styles.asInfoContainer,
                  configBgColor && styles.asInfoContainerWithBg,
                  styles[configElement.name],
                  customClass?.affiliateShopping?.blockContainer,
                  styles[`layout-${configElement.dataSource.key}`],
                )}
                key={k}
                style={{ backgroundColor: configBgColor }}
              >
                <div className={styles.blockContainer}>
                  {mapLayoutElement(block, configElement)}
                </div>
              </section>
            );
          }
        })}
    </>
  );
}
